<template>
  <div class="bg-white py-1 transition-all duration-300 shadow-sm">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
      <!-- Logo区域 -->
      <div class="flex items-center space-x-2 sm:space-x-3">
        <img :src="backgroundImg" alt="Logo" 
          class="w-[40px] h-[35px] sm:w-[50px] sm:h-[45px] lg:w-[60px] lg:h-[50px] object-contain flex-shrink-0"
        />
        <div class="text-black text-[1.1rem] sm:text-[1.3rem] lg:text-[1.5rem] font-bold leading-tight">
          {{ t('navigationBar.name') }}
        </div>
      </div>
      
      <!-- PC端导航菜单 -->
      <div class="hidden lg:flex items-center space-x-6 xl:space-x-8">
        <router-link
          v-for="(nav, index) in navigation"
          :key="index"
          :to="t(`navigationBar.links[${index}].link`)"
          class="text-black hover:text-[#2f5c45] transition-colors duration-200 font-medium text-[14px] xl:text-[15px] relative group"
        >
          {{ t(`navigationBar.links[${index}].name`) }}
          <!-- 悬停下划线效果 -->
          <span class="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#2f5c45] transition-all duration-200 group-hover:w-full"></span>
        </router-link>
      </div>
      
      <!-- 移动端汉堡菜单按钮 -->
      <div class="lg:hidden">
        <button 
          @click.stop="toggleMobileMenu" 
          class="text-black hover:text-[#2f5c45] transition-colors duration-200 p-2 relative z-50"
          :class="{ 'text-[#2f5c45]': showMobileMenu }"
          type="button"
        >
          <svg v-if="!showMobileMenu" class="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
          </svg>
          <svg v-else class="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <!-- 移动端下拉菜单 -->
    <div 
      v-if="showMobileMenu" 
      class="lg:hidden bg-white border-t border-gray-100 animate-slideDown relative z-40"
      @click.stop
    >
      <div class="container mx-auto px-4 py-3 sm:py-4">
        <div class="space-y-1">
          <router-link
            v-for="(nav, index) in navigation"
            :key="index"
            :to="t(`navigationBar.links[${index}].link`)"
            @click="closeMobileMenu"
            class="block py-2.5 sm:py-3 px-3 sm:px-4 text-black hover:text-[#2f5c45] hover:bg-gray-50 transition-all duration-200 font-medium text-[15px] sm:text-[16px] rounded-md"
          >
            {{ t(`navigationBar.links[${index}].name`) }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup name="NavigationBar">
  import { ref, onMounted, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import backgroundImg from '../../../assets/other/logo.png'

  const { t, locale } = useI18n()
  
  // 移动端菜单状态
  const showMobileMenu = ref(false)

  const navigation = ref([
    { name: '首页', link: '/home' },
    { name: '关于我们', link: '/about' },
    { name: '解决方案', link: '/solution' },
    { name: '产品定制', link: '/product' },
    { name: '联系我们', link: '/contact' }
  ])
  
  // 切换移动端菜单
  const toggleMobileMenu = () => {
    console.log('Toggle menu clicked, current state:', showMobileMenu.value)
    showMobileMenu.value = !showMobileMenu.value
    console.log('New state:', showMobileMenu.value)
  }
  
  // 关闭移动端菜单
  const closeMobileMenu = () => {
    showMobileMenu.value = false
  }
  
  // 简化的外部点击处理
  const handleDocumentClick = (e) => {
    // 只有当菜单打开时才处理外部点击
    if (showMobileMenu.value) {
      // 检查点击是否在导航栏外部
      const nav = e.target.closest('.bg-white.py-1')
      if (!nav) {
        showMobileMenu.value = false
      }
    }
  }
  
  // 处理窗口大小变化
  const handleResize = () => {
    if (window.innerWidth >= 1024) {
      showMobileMenu.value = false
    }
  }
  
  onMounted(() => {
    // 延迟添加事件监听器，避免初始化时的冲突
    setTimeout(() => {
      document.addEventListener('click', handleDocumentClick)
      window.addEventListener('resize', handleResize)
    }, 100)
  })
  
  onUnmounted(() => {
    document.removeEventListener('click', handleDocumentClick)
    window.removeEventListener('resize', handleResize)
  })
</script>

<style scoped>
/* 移动端下拉动画 */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
    max-height: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
    max-height: 300px;
  }
}

.animate-slideDown {
  animation: slideDown 0.3s ease-out;
}

/* 移动端字体优化 */
@media (max-width: 640px) {
  .router-link-active {
    color: #2f5c45;
    background-color: #f9fafb;
  }
  
  /* 小屏幕字体调整 */
  .text-\[1\.1rem\] {
    font-size: 1.05rem;
    line-height: 1.3;
  }
  
  .text-\[15px\] {
    font-size: 14px;
  }
}

/* 平板端优化 */
@media (min-width: 641px) and (max-width: 1023px) {
  .router-link-active {
    color: #2f5c45;
    background-color: #f9fafb;
  }
}

/* PC端活跃链接样式 */
@media (min-width: 1024px) {
  .router-link-active {
    color: #2f5c45;
  }
  
  .router-link-active span {
    width: 100%;
  }
}

/* 优化移动端触摸体验 */
@media (hover: none) and (pointer: coarse) {
  .hover\:text-\[\#2f5c45\]:hover {
    color: #2f5c45;
  }
  
  .hover\:bg-gray-50:hover {
    background-color: #f9fafb;
  }
  
  /* 增强移动端点击反馈 */
  .block:active {
    background-color: #e5e7eb;
    transform: scale(0.98);
  }
}

/* 确保按钮可点击 */
button {
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  min-height: 44px; /* 确保足够的触摸目标 */
  min-width: 44px;
}

/* 移动端菜单项触摸优化 */
@media (max-width: 1023px) {
  .block {
    min-height: 44px; /* 确保足够的触摸目标 */
    display: flex;
    align-items: center;
  }
}

/* 超小屏幕优化 */
@media (max-width: 375px) {
  .text-\[1\.1rem\] {
    font-size: 1rem;
  }
  
  .text-\[15px\] {
    font-size: 13px;
  }
  
  .space-x-2 > :not([hidden]) ~ :not([hidden]) {
    margin-left: 0.375rem;
  }
}
</style>
